import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import LandingPage from "./LandingPage";
import LoginForm from "./LoginForm";
import { classNames } from "../../ui/styles/classNames";
import type {
  PageTransitionDetail,
  PageTransitionDirection,
} from "./pageTransition";
import {
  PAGE_TRANSITION_DURATION_MS,
  PAGE_TRANSITION_EVENT
} from "./pageTransition";

type AuthFace = "landing" | "login";

const toFace = (pathname: string): AuthFace => (pathname === "/login" ? "login" : "landing");

const inferDirection = (from: AuthFace, to: AuthFace): PageTransitionDirection => {
  if (from === to) {
    return "right";
  }
  return to === "login" ? "right" : "left";
};

export default function AuthCubeShell() {
  const location = useLocation();
  const [activeFace, setActiveFace] = useState<AuthFace>(() => toFace(location.pathname));
  const [previousFace, setPreviousFace] = useState<AuthFace | null>(null);
  const [direction, setDirection] = useState<PageTransitionDirection>("right");
  const [transitioning, setTransitioning] = useState(false);

  const pendingDirectionRef = useRef<PageTransitionDirection | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleTransitionEvent = (event: Event) => {
      const detail = (event as CustomEvent<PageTransitionDetail | null>).detail;
      if (detail === null) {
        return;
      }
      pendingDirectionRef.current = detail.direction;
    };

    window.addEventListener(PAGE_TRANSITION_EVENT, handleTransitionEvent);
    return () => {
      window.removeEventListener(PAGE_TRANSITION_EVENT, handleTransitionEvent);
    };
  }, []);

  useEffect(() => {
    const nextFace = toFace(location.pathname);
    if (nextFace === activeFace) {
      return;
    }

    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }

    const nextDirection = pendingDirectionRef.current || inferDirection(activeFace, nextFace);
    pendingDirectionRef.current = null;

    setDirection(nextDirection);
    setPreviousFace(activeFace);
    setActiveFace(nextFace);
    setTransitioning(true);

    timerRef.current = window.setTimeout(() => {
      setTransitioning(false);
      setPreviousFace(null);
    }, PAGE_TRANSITION_DURATION_MS + 40);
  }, [activeFace, location.pathname]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const getFaceClassName = (face: AuthFace): string => {
    const isActive = face === activeFace;
    const isOutgoing = transitioning && previousFace === face;
    const isIncoming = transitioning && isActive && previousFace !== null;

    return classNames(
      "app-auth-cube-face",
      `app-auth-cube-face-${face}`,
      isActive && !transitioning && "is-active",
      isOutgoing && "is-outgoing",
      isIncoming && "is-incoming",
      !isActive && !isOutgoing && "is-hidden"
    );
  };

  return (
    <div
      className={classNames(
        "app-auth-cube-shell",
        `app-auth-cube-dir-${direction}`,
        transitioning && "is-transitioning"
      )}
    >
      <div className="app-auth-cube-stage">
        <div className={getFaceClassName("landing")} aria-hidden={activeFace !== "landing"}>
          <LandingPage />
        </div>
        <div className={getFaceClassName("login")} aria-hidden={activeFace !== "login"}>
          <LoginForm />
        </div>
      </div>
    </div>
  );
}
